import _, { noop } from "lodash";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";

import { CREATE_LINE_ITEM } from "@/apis/gql/apolloClient";
import type {
  AccountDetails,
  LineItem,
  LineItemDetails,
  TaxCode,
} from "@/apis/gql/types";
import { type ApolloError, gql, useMutation } from "@apollo/client";

import { LINE_ITEMS_QUERY } from "./fetch";

type CreateLineItem = {
  invoiceId?: string;
  lineItem: {
    index: number;
    amount: number;
    nettoAmount: number;
    name: string;
    vatRate: number;
    accountId: string;
    taxCodeId: string;
    bookingTextManuallyUpdated: boolean;
  };
};

export const useCreateLineItem = (): [
  (
    data: CreateLineItem,
    onSuccess?: () => void,
    onError?: () => void,
  ) => Promise<LineItem>,
  {
    data: any;
    loading: boolean;
    error?: ApolloError;
  },
] => {
  const { t } = useTranslation();
  const [createLineItem, { data, loading, error }] =
    useMutation(CREATE_LINE_ITEM);

  const handleCreate = async (
    data: CreateLineItem,
    onSuccess: () => void = noop,
    onError: () => void = noop,
  ) => {
    if (!data.invoiceId) throw new Error("Invoice ID is required");
    const toastId = toast.loading(t("generic_loading"));
    const invoiceId = data.invoiceId;
    const input = { invoiceId, createFields: data.lineItem };
    const lineItem = data.lineItem;

    try {
      const response = await createLineItem({
        variables: input,
        optimisticResponse: {
          __typename: "Mutation",
          createLineItem: {
            __typename: "LineItemDetails",
            lineItemDetailsId: uuidv4(),
            lineItem: {
              __typename: "LineItem",
              amount: lineItem?.amount ?? null,
              nettoAmount: lineItem?.nettoAmount ?? null,
              name: lineItem?.name ?? null,
              bookingTextManuallyUpdated:
                lineItem?.bookingTextManuallyUpdated ?? false,
              vatRate: lineItem?.vatRate ?? null,
              lineItemId: uuidv4(),
              aiDescription: null,
              invoiceId: data.invoiceId,
              startDate: null,
              endDate: null,
              accountJustification: null,
              index: lineItem?.index ?? null,
              description: null,
              accountConfidence: null,
              vatRateConfidence: null,
              accountReviewed: null,
              accountId: lineItem?.accountId ?? null,
              amountConfidence: null,
              taxCodeId: lineItem?.taxCodeId ?? null,
              buJustification: null,
              taxCodeConfidence: null,
            },
            expenseAccount: {
              __typename: "Account",
              accountId: null,
              accountName: null,
              accountNo: null,
              accountDescription: null,
              automatic: null,
              taxCodeId: null,
            },
            taxCode: {
              __typename: "TaxCode",
              taxCodeId: null,
              name: null,
              description: null,
              datevBu: null,
              vatRate: null,
            },
          },
        },
        update: (cache, { data: { createLineItem } }) => {
          const newLineItem: LineItemDetails = structuredClone(createLineItem);

          const accountCacheId = cache.identify({
            __typename: "Account",
            accountId: lineItem?.accountId,
          });
          const cachedAccount: AccountDetails | null = cache.readFragment({
            id: accountCacheId,
            fragment: gql`
              fragment ExpenseAccount on Account {
                accountId
                accountName
                accountNo
                accountDescription
                automatic
                taxCodeId
              }
            `,
          });

          if (
            newLineItem &&
            !newLineItem?.expenseAccount?.accountId &&
            !_.isEmpty(cachedAccount)
          ) {
            newLineItem.expenseAccount = cachedAccount;
          }

          const taxCodeCacheId = cache.identify({
            __typename: "TaxCode",
            taxCodeId: lineItem?.taxCodeId ?? cachedAccount?.taxCodeId,
          });

          const cachedTaxCode: TaxCode | null = cache.readFragment({
            id: taxCodeCacheId,
            fragment: gql`
              fragment TaxCode on TaxCode {
                taxCodeId
                name
                description
                datevBu
                vatRate
              }
            `,
          });

          // Set tax code and vat rate
          let vatRate = newLineItem?.lineItem?.vatRate ?? 0;
          if (
            newLineItem &&
            !newLineItem?.taxCode?.taxCodeId &&
            !_.isEmpty(cachedTaxCode)
          ) {
            newLineItem.taxCode = cachedTaxCode;
            vatRate = vatRate || cachedTaxCode.vatRate;
          }

          if (newLineItem?.lineItem) newLineItem.lineItem.vatRate = vatRate;

          // Optimistic brutto / netto conversion
          const { amount, nettoAmount } = newLineItem?.lineItem || {};
          if (amount && !nettoAmount) {
            newLineItem.lineItem.nettoAmount = amount / (1 + vatRate / 100);
          } else if (nettoAmount && !amount) {
            newLineItem.lineItem.amount = nettoAmount * (1 + vatRate / 100);
          }

          const queryData: { lineItemDetails: LineItemDetails[] } | null =
            cache.readQuery({
              query: LINE_ITEMS_QUERY,
              variables: { invoiceId: data.invoiceId, paranoid: true },
            });

          if (queryData?.lineItemDetails) {
            cache.writeQuery({
              query: LINE_ITEMS_QUERY,
              data: {
                lineItemDetails: [...queryData.lineItemDetails, newLineItem],
              },
              variables: { invoiceId: data.invoiceId, paranoid: true },
            });
          }
        },
      });

      if (response.errors) {
        throw new Error("error creating line item");
      }
      onSuccess();
      toast.success(t("generic_success"), { id: toastId });
      return response.data?.createLineItem.lineItem;
    } catch (error) {
      onError();
      toast.error(t("generic_error"), { id: toastId, richColors: true });
      throw error;
    }
  };

  return [handleCreate, { data, loading, error }];
};
